/**
 * AccommodationApply — applying for a room, without an account.
 *
 * The form asks who you are and when you are coming, and *then* tells you what
 * it will need from you — and, just as deliberately, what it will not. Somebody
 * staying two nights sees "identity, and sign the house rules", not a wall of
 * clearances they would close the tab over.
 *
 * Route: /accommodation/apply
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2, Check, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccommodationApply() {
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', category: 'guest', campus_id: '', campus_label: '',
    arrival: '', departure: '', accommodation_type: '', party_size: 1,
    accessibility_needs: '', notes: '', under_18: false, regulated_contact: false,
    bringing_children: false, agreement_accepted: false,
    event_priority_accepted: false, privacy_accepted: false,
    emergency_contact: { name: '', phone: '', relationship: '' },
  });
  const [checks, setChecks] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [uploads, setUploads] = useState({});

  const upload = async (code, file) => {
    const body = new FormData();
    body.append('email', form.email);
    body.append('code', code);
    body.append('file', file);
    setUploads((u) => ({ ...u, [code]: 'sending…' }));
    try {
      const { data } = await axios.post(
        `${API_URL}/api/public/accommodation/applications/${done.reference}/documents`,
        body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploads((u) => ({ ...u, [code]: `received · ${data.filename}` }));
      toast.success('Thank you — that is with us', { description: data.note });
    } catch (e) {
      setUploads((u) => ({ ...u, [code]: '' }));
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  React.useEffect(() => {
    axios.get(`${API_URL}/api/public/accommodation/properties`)
      .then(({ data }) => {
        setProperties(data.properties || []);
        if ((data.properties || []).length) {
          setForm((f) => ({ ...f, campus_id: data.properties[0].id,
            campus_label: data.properties[0].name }));
        }
      })
      .catch(() => setProperties([]));
  }, []);

  const set = (patch) => { setForm({ ...form, ...patch }); setChecks(null); };

  const datesReady = form.arrival && form.departure && form.departure > form.arrival;

  const seeRequirements = async () => {
    try {
      const { data } = await axios.post(`${API_URL}/api/public/accommodation/requirements`, {
        category: form.category, arrival: form.arrival, departure: form.departure,
        campus_id: form.campus_id, under_18: form.under_18,
        regulated_contact: form.regulated_contact, bringing_children: form.bringing_children,
      });
      setChecks(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/public/accommodation/applications`, form);
      setDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  if (done) {
    return (      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6">
        <Card className="max-w-xl w-full" data-testid="application-received">
          <CardHeader>
            <CardTitle className="text-2xl">Thank you, {done.full_name.split(' ')[0]}</CardTitle>
            <CardDescription>
              Your reference is <strong data-testid="application-reference">{done.reference}</strong>.
              Keep it — with your email address it will show you where the application has got to.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-stone-700">
            <p>
              {done.category_label} at {done.campus_id}, {done.arrival} to {done.departure}
              {' '}({done.nights} night{done.nights === 1 ? '' : 's'}).
            </p>
            {done.outstanding?.length > 0 && (
              <div className="space-y-3">
                <p className="font-medium text-stone-900">Still to come from you:</p>
                <ul className="space-y-3">
                  {done.outstanding.map((o) => (
                    <li key={o.code} className="space-y-1" data-testid={`outstanding-${o.code}`}>
                      <p><strong>{o.label}</strong> — {o.why}</p>
                      {['identity', 'police_clearance', 'safeguarding_clearance', 'guardian_consent',
                        'references', 'right_to_rent', 'enrolment', 'external_enrolment',
                        'employment', 'ministry_standing'].includes(o.code) && (
                        <div className="flex items-center gap-2">
                          <input type="file" accept=".pdf,image/*" data-testid={`upload-${o.code}`}
                            className="text-xs text-stone-600"
                            onChange={(e) => e.target.files?.[0] && upload(o.code, e.target.files[0])} />
                          {uploads[o.code] && (
                            <span className="text-xs text-stone-600" data-testid={`uploaded-${o.code}`}>
                              {uploads[o.code]}
                            </span>
                          )}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-stone-500">
                  Anything you send is kept privately, seen only by the housing staff who need it,
                  and deleted once there is no longer a reason to hold it.
                </p>
              </div>
            )}
            <p className="text-stone-500">
              An application is not yet a room. The housing office will be in touch once they have
              looked at it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <header className="space-y-2">
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900">
            Apply for a room
          </h1>
          <p className="text-base text-stone-600">
            Students, staff, ministry workers, delegates and visitors. Tell us who you are and when
            you are coming, and we will show you exactly what we need — no more than the stay warrants.
          </p>
        </header>

        <Card>
          <CardHeader><CardTitle className="text-lg">Who is coming</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Full name</Label><Input value={form.full_name} data-testid="apply-name"
                onChange={(e) => set({ full_name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} data-testid="apply-email"
                onChange={(e) => set({ email: e.target.value })} /></div>
              <div><Label>Phone</Label><Input value={form.phone} data-testid="apply-phone"
                onChange={(e) => set({ phone: e.target.value })} /></div>
              <div>
                <Label>You are a…</Label>
                <Select value={form.category} onValueChange={(v) => set({ category: v })}>
                  <SelectTrigger data-testid="apply-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="guest">Overnight guest</SelectItem>
                    <SelectItem value="visitor">Visitor</SelectItem>
                    <SelectItem value="delegate">Event delegate</SelectItem>
                    <SelectItem value="student">Bible College student</SelectItem>
                    <SelectItem value="external_student">Student of another institution</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="lecturer">Lecturer</SelectItem>
                    <SelectItem value="minister">Church or ministry worker</SelectItem>
                    <SelectItem value="tenant">External tenant</SelectItem>
                    <SelectItem value="family">Family or group</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Where</Label>
                <Select value={form.campus_id}
                  onValueChange={(v) => set({ campus_id: v,
                    campus_label: (properties.find((p) => p.id === v) || {}).name || '' })}>
                  <SelectTrigger data-testid="apply-campus">
                    <SelectValue placeholder={properties.length ? 'Choose' : 'Loading…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Arriving</Label><Input type="date" value={form.arrival} data-testid="apply-arrival"
                onChange={(e) => set({ arrival: e.target.value })} /></div>
              <div><Label>Leaving</Label><Input type="date" value={form.departure} data-testid="apply-departure"
                onChange={(e) => set({ departure: e.target.value })} /></div>
            </div>

            <div className="space-y-2 pt-2">
              {[['under_18', 'I am under 18'],
                ['bringing_children', 'Children will be staying with me'],
                ['regulated_contact', 'The role I am coming for involves working with children or vulnerable adults']
              ].map(([key, label]) => (
                <label key={key} className="flex items-start gap-3 text-sm text-stone-700 cursor-pointer">
                  <Checkbox checked={form[key]} onCheckedChange={(v) => set({ [key]: !!v })}
                    data-testid={`apply-${key}`} />
                  <span>{label}</span>
                </label>
              ))}
            </div>

            <Button variant="outline" onClick={seeRequirements} disabled={!datesReady}
              data-testid="see-requirements">What will you need from me?</Button>
          </CardContent>
        </Card>

        {checks && (
          <Card data-testid="requirements-preview">
            <CardHeader>
              <CardTitle className="text-lg">
                {checks.nights} night{checks.nights === 1 ? '' : 's'} — {checks.is_residency ? 'a residency' : 'a visit'}
              </CardTitle>
              <CardDescription>
                {checks.requirements.length} thing{checks.requirements.length === 1 ? '' : 's'} we will need.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2">
                {checks.requirements.map((r) => (
                  <li key={r.code} className="flex items-start gap-3 text-sm" data-testid={`req-${r.code}`}>
                    <Check className="h-4 w-4 text-stone-900 mt-0.5 shrink-0" />
                    <span><strong className="text-stone-900">{r.label}</strong>
                      <span className="text-stone-600"> — {r.why}</span></span>
                  </li>
                ))}
              </ul>
              {checks.not_required?.length > 0 && (
                <div className="bg-stone-100 rounded p-3 space-y-1" data-testid="not-required">
                  <p className="text-sm font-medium text-stone-900 flex items-center gap-2">
                    <Info className="h-4 w-4" /> What we are not asking you for
                  </p>
                  {checks.not_required.map((n, i) => (
                    <p key={i} className="text-sm text-stone-600">{n}</p>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader><CardTitle className="text-lg">A few last things</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div><Label>Emergency contact</Label><Input value={form.emergency_contact.name}
                data-testid="apply-emergency-name"
                onChange={(e) => set({ emergency_contact: { ...form.emergency_contact, name: e.target.value } })} /></div>
              <div><Label>Their phone</Label><Input value={form.emergency_contact.phone}
                data-testid="apply-emergency-phone"
                onChange={(e) => set({ emergency_contact: { ...form.emergency_contact, phone: e.target.value } })} /></div>
              <div><Label>Relationship</Label><Input value={form.emergency_contact.relationship}
                data-testid="apply-emergency-relationship"
                onChange={(e) => set({ emergency_contact: { ...form.emergency_contact, relationship: e.target.value } })} /></div>
            </div>
            <div><Label>Anything we should know about access or mobility</Label>
              <Input value={form.accessibility_needs} data-testid="apply-accessibility"
                onChange={(e) => set({ accessibility_needs: e.target.value })} /></div>
            <Textarea placeholder="Anything else" value={form.notes} data-testid="apply-notes"
              onChange={(e) => set({ notes: e.target.value })} />

            <div className="space-y-3 border-t border-stone-200 pt-4">
              <label className="flex items-start gap-3 text-sm text-stone-700 cursor-pointer">
                <Checkbox checked={form.agreement_accepted} data-testid="accept-agreement"
                  onCheckedChange={(v) => setForm({ ...form, agreement_accepted: !!v })} />
                <span>I accept the accommodation agreement and the campus residence rules — respectful
                  behaviour, visitor and quiet hours, fire safety, no unauthorised subletting, and no
                  commercial food sales without a permit.</span>
              </label>
              <label className="flex items-start gap-3 text-sm text-stone-700 cursor-pointer">
                <Checkbox checked={form.event_priority_accepted} data-testid="accept-event-priority"
                  onCheckedChange={(v) => setForm({ ...form, event_priority_accepted: !!v })} />
                <span>I understand that campus rooms are sometimes needed for conferences and
                  conventions, and that I may be asked to move for a period with proper notice and an
                  alternative room where one is available.
                  <Badge variant="secondary" className="ml-2">Optional</Badge></span>
              </label>
              <label className="flex items-start gap-3 text-sm text-stone-700 cursor-pointer">
                <Checkbox checked={form.privacy_accepted} data-testid="accept-privacy"
                  onCheckedChange={(v) => setForm({ ...form, privacy_accepted: !!v })} />
                <span>I understand my details will be used to manage this accommodation, and that
                  documents I provide are seen only by the staff who need them.</span>
              </label>
              {!form.event_priority_accepted && (
                <p className="text-sm text-stone-500" data-testid="priority-note">
                  You can leave the conference term unticked. It simply means we will not ask you to
                  move for an event during this stay.
                </p>
              )}
            </div>

            <Button onClick={submit} data-testid="submit-application"
              disabled={busy || !form.full_name || !form.email || !datesReady
                || !form.campus_id || !form.agreement_accepted || !form.privacy_accepted}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send the application'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
